import React from "react";
import { useToast } from "../ResultsPrimitives.jsx";
import { truckNumber, workDateLabel } from "../../utils/consolidationPresentation.js";
import { createToastProgress, createToastProgressReporter, runToastProgressOperationOnce } from "../../utils/toastProgress.js";

const performedOrdersMessage = (row) => {
  const count = new Set(row.validated_calculation?.performed_order_ids ?? []).size;
  return `${count} commande${count === 1 ? "" : "s"} marquée${count === 1 ? "" : "s"} livrée${count === 1 ? "" : "s"}.`;
};

export function useActionFeedback(run, row) {
  const { showToast, updateToast, dismissToast } = useToast();
  const [error, setError] = React.useState(null);
  const saves = React.useRef(new Map());
  const isSheet = Array.isArray(row.visits);
  const label = isSheet ? "Relevé" : "Préchargement";
  const trucks = isSheet ? [truckNumber(row)] : [...new Set(row.links.map(truckNumber))];
  const scope = `${row.driver_name || row.employee_name || `Camion${trucks.length > 1 ? "s" : ""} ${trucks.join(", ")}`} · ${workDateLabel(row.dispatch_date || row.delivery_date)}${row.dispatch_period === "evening" ? " · Soir" : ""}`;
  const notify = (type, title, message, toastId = null) => {
    const toast = { type, title, message: [scope, message].filter(Boolean).join(" · "),
      duration: type === "error" ? 8000 : type === "info" ? 5000 : 4500 };
    if (toastId) updateToast(toastId, toast); else showToast(toast);
    return toast;
  };
  const clearError = () => setError(null);
  // onSettled reçoit l'issue finale et le toast montré (Captures) ; il ne change ni les toasts ni l'erreur affichée.
  const execute = (work, action, { refresh, onError, needsSave = false, onSettled } = {}) => run(async () => {
    const settled = { state: "failed", error: null, toast: null };
    setError(null);
    const trackedSave = action === "save" && typeof refresh === "function";
    const trackedValidation = action === "validate" && isSheet && typeof refresh === "function";
    const progress = createToastProgress(trackedSave || trackedValidation ? [
      ...(!row.id ? [{ key: "verify", label: "Vérification de la journée" },
        { key: "photo", label: "Conservation de la photo du Dispatch" }] : []),
      ...(trackedSave || needsSave ? [{ key: "save", label: `Enregistrement ${isSheet ? "du relevé" : "du préchargement"}` }] : []),
      ...(trackedValidation ? [{ key: "validate", label: "Validation du relevé" }] : []),
      { key: "refresh", label: "Actualisation de l’affichage" },
    ] : null);
    const pendingTitle = { save: "Enregistrement en cours…", validate: "Validation en cours…",
      reopen: "Réouverture en cours…", "execution-correction": "Correction en cours…" }[action];
    let pendingToastId = pendingTitle ? showToast({ type: "loading", title: pendingTitle, message: scope, duration: null, progress }) : null;
    const report = createToastProgressReporter(progress, next => updateToast(pendingToastId, { progress: next }));
    const finish = (type, title, message) => {
      settled.toast = notify(type, title, message, pendingToastId);
      pendingToastId = null;
    };
    try {
      // Existing actions receive no argument: save's first argument is a boolean.
      const result = trackedSave || trackedValidation ? await work(report) : await work();
      if (trackedSave || trackedValidation) {
        report(trackedValidation ? "validate" : "save", "completed");
        report("refresh");
        updateToast(pendingToastId, { message: `${scope} · ${label} ${trackedValidation ? "validé" : "enregistré"}. Actualisation de l’affichage…` });
        try {
          await refresh();
          report("refresh", "completed");
        } catch (failure) {
          report("refresh", "failed");
          report.finish({ status: "failed" });
          finish("warning", trackedValidation ? "Relevé validé — actualisation impossible" : "Enregistré — actualisation impossible", failure.message || "Rechargez la journée.");
          Object.assign(settled, { state: "refresh_failed", error: failure });
          return result;
        }
      }
      report.finish();
      if (action === "save") finish("success", `${label} enregistré`);
      else if (action === "validate") finish("success", `${label} validé`, isSheet ? performedOrdersMessage(result) : null);
      else if (action === "reopen") finish("success", `${label} rouvert pour modification`);
      settled.state = "success";
      return result;
    } catch (failure) {
      report.finish({ status: "failed" });
      settled.error = failure;
      if (onError?.(failure)) {
        settled.state = "handled";
        return;
      }
      setError(failure);
      // Dit dans le toast et sous le relevé : pas de cadre rouge en haut de la page.
      if (failure && typeof failure === "object") failure.shown = true;
      if (action !== "preview") finish("error", failure.saveUnconfirmed ? "Sauvegarde non confirmée" : "Action impossible", failure.message || "Une erreur est survenue.");
      if (trackedSave && (failure.saveUnconfirmed || failure.status === 409)) {
        try { await refresh(); } catch { /* Preserve the original error and the draft. */ }
      }
      throw failure;
    } finally {
      if (pendingToastId) dismissToast(pendingToastId);
      try { onSettled?.({ ...settled, steps: report.getSnapshot()?.steps || [] }); } catch { /* Captures only. */ }
    }
  });
  const save = (work, refresh, onSettled) => runToastProgressOperationOnce(saves.current, "save", () => execute(work, "save", { refresh, onSettled }));
  return { error, execute, save, clearError };
}

export default function ActionFeedback({ error, row, children }) {
  if (error) return <><p role="alert" className="w-full rounded-[4px] border border-red-200 bg-red-50 p-2 text-xs text-red-800">{error.message}</p>{children}</>;
  if (row.status !== "validated") return null;
  return <div className="flex w-full items-center gap-2 rounded-[4px] border border-accent bg-accent p-2 text-xs text-white">
    <p role="status" className="min-w-0 flex-1">{row.visits ? `Relevé validé · ${performedOrdersMessage(row)}` : "Préchargement validé."}</p>
    {children}
  </div>;
}
